/**
 * AURA Floating Action Button (FAB)
 * The accessibility icon that opens the panel
 */

import { AuraConfig } from '../core/types';
import { stateManager } from '../core/state';
import { LOGO_ICON_TRANSPARENT } from './styles/logo-data';

const FAB_STYLES = `
  .aura-fab {
    position: fixed;
    width: var(--aura-fab-size);
    height: var(--aura-fab-size);
    border-radius: var(--aura-radius-full);
    background: var(--aura-fab-bg);
    color: var(--aura-text-inverse);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: var(--aura-shadow-lg);
    transition: transform var(--aura-transition), background-color var(--aura-transition);
    z-index: var(--aura-z-fab);
  }

  .aura-fab:hover {
    background: var(--aura-fab-bg-hover);
    transform: scale(1.05);
  }

  .aura-fab:focus-visible {
    outline: 3px solid var(--aura-border-focus);
    outline-offset: 3px;
    background: var(--aura-fab-bg);
  }

  .aura-fab:active {
    transform: scale(0.95);
  }

  .aura-fab--bottom-right {
    bottom: var(--aura-fab-bottom-offset, 50px);
    right: var(--aura-spacing-lg);
  }

  .aura-fab--bottom-left {
    bottom: var(--aura-fab-bottom-offset, 50px);
    left: var(--aura-spacing-lg);
  }

  .aura-fab--top-right {
    top: var(--aura-spacing-lg);
    right: var(--aura-spacing-lg);
  }

  .aura-fab--top-left {
    top: var(--aura-spacing-lg);
    left: var(--aura-spacing-lg);
  }

  .aura-fab__icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .aura-fab__icon img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .aura-fab--active {
    animation: aura-pulse 2s infinite;
  }

  .aura-fab--hidden {
    display: none;
  }

  @keyframes aura-pulse {
    0%, 100% {
      box-shadow: var(--aura-shadow-lg);
    }
    50% {
      box-shadow: var(--aura-shadow-lg), 0 0 0 8px rgba(30, 58, 95, 0.2);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .aura-fab--active {
      animation: none;
    }
  }
`;

export function createFab(config: AuraConfig): {
  element: HTMLElement;
  styles: string;
  destroy: () => void;
} {
  const button = document.createElement('button');
  button.className = `aura-fab aura-fab--${config.position || 'bottom-right'}`;
  button.setAttribute('type', 'button');
  button.setAttribute('aria-label', 'Open accessibility settings');
  button.setAttribute('aria-expanded', 'false');
  button.setAttribute('aria-haspopup', 'dialog');
  button.setAttribute('data-aura', 'fab');

  button.innerHTML = `
    <span class="aura-fab__icon" aria-hidden="true">
      <img src="${LOGO_ICON_TRANSPARENT}" alt="" />
    </span>
  `;

  button.addEventListener('click', () => {
    const isOpen = stateManager.getState().isOpen;
    stateManager.setPanelOpen(!isOpen);
  });

  const unsubscribe = stateManager.subscribe((state) => {
    button.setAttribute('aria-expanded', String(state.isOpen));
    button.classList.toggle('aura-fab--hidden', state.isOpen || state.isHidden);
    button.classList.toggle('aura-fab--active', state.activeProfile !== null);
  });

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.altKey && e.key === 'F9') {
      e.preventDefault();
      if (stateManager.getState().isHidden) {
        stateManager.setHidden(false);
      }
    }
  };
  document.addEventListener('keydown', handleKeydown);

  const destroy = () => {
    document.removeEventListener('keydown', handleKeydown);
    unsubscribe();
  };

  return { element: button, styles: FAB_STYLES, destroy };
}
